import { useCallback, useEffect, useState } from "react";
import AppLayout from "../components/AppLayout";
import PageHero from "../components/PageHero";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Inbox, AlertCircle, Lightbulb, Trash2, Clock, CheckCircle2, Circle, Reply, Send, CheckCheck } from "lucide-react";
import { useSiteTime } from "../lib/time";
import { Textarea } from "../components/ui/textarea";

const TEAL = "#0D9488";

const STATUS_META = {
  new:       { label: "New",       Icon: Circle,        color: "#0D9488", chip: "bg-teal-50 text-teal-800 border-teal-200 dark:bg-teal-900/25 dark:text-teal-200" },
  reviewing: { label: "Reviewing", Icon: Clock,         color: "#F59E0B", chip: "bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-900/25 dark:text-amber-200" },
  resolved:  { label: "Resolved",  Icon: CheckCircle2,  color: "#10B981", chip: "bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-900/25 dark:text-emerald-200" },
};

const TYPE_META = {
  problem: { label: "Problem", Icon: AlertCircle, color: "#EF4444" },
  idea:    { label: "Idea",    Icon: Lightbulb,   color: "#0D9488" },
};

function StatBadge({ label, value, color }) {
  return (
    <div className="border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-lg p-3 flex items-baseline gap-2">
      <div className="text-2xl font-bold tabular-nums" style={{ color }}>{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 dark:text-slate-400 font-mono-num">{label}</div>
    </div>
  );
}

/**
 * /admin/feedback — private admin inbox for the "Send Feedback" flow.
 *
 * Only admins hit this route (server enforces `require_admin`, and the
 * frontend route is `adminOnly`). Coaches and moderators cannot see it.
 *
 * Layout:
 *   - Header stats: total, new, reviewing, resolved
 *   - Filter pills: All / New / Reviewing / Resolved
 *   - List of feedback cards (stacked, mobile-friendly)
 */
export default function AdminFeedback() {
  const { fmtDateTime } = useSiteTime();
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ new: 0, reviewing: 0, resolved: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState(""); // "" = all
  const [replyingId, setReplyingId] = useState(null);   // which row's composer is open
  const [replyText, setReplyText] = useState("");
  const [sendingReply, setSendingReply] = useState(false);

  const openReply = (id) => {
    setReplyingId(id);
    setReplyText("");
  };
  const closeReply = () => {
    setReplyingId(null);
    setReplyText("");
  };
  const sendReply = async (id) => {
    const msg = replyText.trim();
    if (msg.length < 3) { toast.error("Reply is too short."); return; }
    setSendingReply(true);
    try {
      const { data } = await api.post(`/admin/feedback/${id}/reply`, { message: msg });
      if (data.email_sent) {
        toast.success("Reply sent — email is on its way");
      } else {
        toast.warning(`Reply saved, but email delivery failed: ${data.email_error || "unknown reason"}`);
      }
      setRows((rs) => rs.map((r) => (r.id === id ? data.row : r)));
      closeReply();
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSendingReply(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      const { data } = await api.get("/admin/feedback", { params });
      setRows(data.rows || []);
      setCounts(data.counts || { new: 0, reviewing: 0, resolved: 0, total: 0 });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);
  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, next) => {
    // Optimistic — flip immediately, snap back on error.
    const prev = rows.slice();
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, status: next } : r)));
    try {
      await api.patch(`/admin/feedback/${id}`, { status: next });
      toast.success(`Marked ${STATUS_META[next].label.toLowerCase()}`);
      load();
    } catch (err) {
      setRows(prev);
      toast.error(formatApiError(err));
    }
  };

  const removeRow = async (id) => {
    if (!window.confirm("Delete this feedback item? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/feedback/${id}`);
      setRows((rs) => rs.filter((r) => r.id !== id));
      toast.success("Feedback deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const FILTERS = [
    { key: "",          label: "All",       n: counts.total },
    { key: "new",       label: "New",       n: counts.new },
    { key: "reviewing", label: "Reviewing", n: counts.reviewing },
    { key: "resolved",  label: "Resolved",  n: counts.resolved },
  ];

  return (
    <AppLayout>
      <PageHero
        eyebrow="Admin"
        title="Feedback Inbox"
        subtitle="Private submissions from users. Only admins can view — coaches and moderators cannot access this page."
        icon={<Inbox className="w-8 h-8" style={{ color: TEAL }} />}
      />

      {/* Stats row — mobile stacks 2×2, desktop is 1×4 */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4" data-testid="feedback-stats">
        <StatBadge label="Total"     value={counts.total}     color="#0F172A" />
        <StatBadge label="New"       value={counts.new}       color={STATUS_META.new.color} />
        <StatBadge label="Reviewing" value={counts.reviewing} color={STATUS_META.reviewing.color} />
        <StatBadge label="Resolved"  value={counts.resolved}  color={STATUS_META.resolved.color} />
      </div>

      {/* Filter pills — horizontally scrollable on mobile */}
      <div className="flex gap-1.5 mb-4 overflow-x-auto pb-1 -mx-1 px-1" data-testid="feedback-filters">
        {FILTERS.map(({ key, label, n }) => {
          const active = statusFilter === key;
          return (
            <button
              key={key || "all"}
              type="button"
              onClick={() => setStatusFilter(key)}
              data-testid={`feedback-filter-${key || "all"}`}
              className={`shrink-0 px-3 h-9 rounded-full border text-xs font-semibold uppercase tracking-widest transition-colors ${
                active
                  ? "bg-[#0D9488] border-[#0D9488] text-white"
                  : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:border-teal-300"
              }`}
            >
              {label} <span className={`ml-1 font-mono-num ${active ? "text-white/80" : "text-slate-400"}`}>{n}</span>
            </button>
          );
        })}
      </div>

      {/* Feedback list */}
      {loading ? (
        <Card className="p-6 text-center text-slate-500 dark:text-slate-400 text-sm">Loading feedback…</Card>
      ) : rows.length === 0 ? (
        <Card className="p-8 text-center border border-dashed border-slate-200 dark:border-slate-700 bg-slate-50/40 dark:bg-slate-900" data-testid="feedback-empty">
          <Inbox className="w-8 h-8 mx-auto mb-2 text-slate-300 dark:text-slate-600" />
          <div className="text-sm font-semibold text-slate-700 dark:text-slate-200">Inbox is quiet</div>
          <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            {statusFilter ? "Try a different filter above." : "New feedback lands here as soon as users send it."}
          </div>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="feedback-list">
          {rows.map((r) => {
            const T = TYPE_META[r.type] || TYPE_META.idea;
            const S = STATUS_META[r.status] || STATUS_META.new;
            return (
              <Card
                key={r.id}
                className="border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-xl p-4 sm:p-5"
                data-testid={`feedback-row-${r.id}`}
              >
                {/* Meta row */}
                <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <T.Icon className="w-4 h-4 shrink-0" style={{ color: T.color }} />
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">{r.user_name}</div>
                      <div className="text-[11px] text-slate-500 dark:text-slate-400 truncate">{r.user_email}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-[10px] uppercase tracking-widest font-mono-num px-2 py-0.5 border rounded-full ${S.chip}`} data-testid={`feedback-status-${r.id}`}>
                      <S.Icon className="w-3 h-3 inline mr-1 -mt-0.5" />
                      {S.label}
                    </span>
                    <span className="text-[10px] uppercase tracking-widest font-mono-num text-slate-500 dark:text-slate-400 hidden sm:inline">
                      {T.label}
                    </span>
                  </div>
                </div>

                {/* Message body */}
                <div className="text-sm text-slate-800 dark:text-slate-200 whitespace-pre-wrap leading-relaxed mb-3" data-testid={`feedback-message-${r.id}`}>
                  {r.message}
                </div>

                {/* Existing admin reply (if any) */}
                {r.admin_reply && (
                  <div className="mb-3 rounded-lg border-l-4 border-teal-500 bg-teal-50/60 dark:bg-teal-900/15 p-3" data-testid={`feedback-existing-reply-${r.id}`}>
                    <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest text-teal-800 dark:text-teal-300 mb-1">
                      <CheckCheck className="w-3 h-3" />
                      Replied {r.replied_at ? `· ${fmtDateTime(r.replied_at)}` : ""} {r.replied_by ? `by ${r.replied_by}` : ""}
                    </div>
                    <div className="text-sm text-slate-800 dark:text-slate-200 whitespace-pre-wrap leading-relaxed">
                      {r.admin_reply}
                    </div>
                  </div>
                )}

                {/* Reply composer */}
                {replyingId === r.id && (
                  <div className="mb-3 rounded-lg border border-teal-200 dark:border-teal-700 bg-white dark:bg-slate-900 p-3" data-testid={`feedback-reply-composer-${r.id}`}>
                    <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1.5">
                      Replying to {r.user_name} · <span className="font-mono-num">{r.user_email}</span>
                    </div>
                    <Textarea
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value.slice(0, 4000))}
                      rows={4}
                      autoFocus
                      placeholder={
                        r.type === "problem"
                          ? "Thanks for flagging this — here's what we're doing about it…"
                          : "Love this idea. Here's how we're thinking about it…"
                      }
                      className="bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 rounded-md resize-none text-sm"
                      data-testid={`feedback-reply-input-${r.id}`}
                    />
                    <div className="flex flex-wrap items-center justify-between gap-2 mt-2">
                      <div className="text-[10px] text-slate-400 dark:text-slate-500 font-mono-num">
                        {replyText.length} / 4000 · sends via email to {r.user_email}
                      </div>
                      <div className="flex gap-1.5">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={closeReply}
                          disabled={sendingReply}
                          className="h-8 px-3 text-xs rounded-md"
                          data-testid={`feedback-reply-cancel-${r.id}`}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="button"
                          onClick={() => sendReply(r.id)}
                          disabled={sendingReply || replyText.trim().length < 3}
                          className="h-8 px-3 text-xs bg-[#0D9488] hover:bg-[#0F766E] text-white rounded-md font-semibold"
                          data-testid={`feedback-reply-send-${r.id}`}
                        >
                          <Send className="w-3.5 h-3.5 mr-1" />
                          {sendingReply ? "Sending…" : "Send reply"}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Footer: date + status controls */}
                <div className="flex flex-wrap items-center justify-between gap-2 pt-3 border-t border-slate-100 dark:border-slate-800">
                  <div className="text-[11px] text-slate-500 dark:text-slate-400 font-mono-num">
                    {r.created_at ? fmtDateTime(r.created_at) : "—"}
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => (replyingId === r.id ? closeReply() : openReply(r.id))}
                      className="h-8 px-2.5 rounded-md border-slate-200 dark:border-slate-700 text-teal-700 hover:border-teal-400 hover:bg-teal-50 dark:text-teal-300 dark:hover:bg-teal-900/20 text-[11px] font-semibold uppercase tracking-widest"
                      data-testid={`feedback-reply-btn-${r.id}`}
                    >
                      <Reply className="w-3.5 h-3.5 mr-1" />
                      {r.admin_reply ? "Reply again" : "Reply"}
                    </Button>
                    {["new", "reviewing", "resolved"].map((s) => {
                      const active = r.status === s;
                      const M = STATUS_META[s];
                      return (
                        <button
                          key={s}
                          type="button"
                          onClick={() => !active && updateStatus(r.id, s)}
                          disabled={active}
                          data-testid={`feedback-set-${s}-${r.id}`}
                          className={`px-2.5 h-8 rounded-md text-[11px] font-semibold uppercase tracking-widest border transition-colors ${
                            active
                              ? "bg-slate-100 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-400 cursor-default"
                              : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:border-teal-300"
                          }`}
                        >
                          <M.Icon className="w-3 h-3 inline mr-1 -mt-0.5" style={{ color: active ? undefined : M.color }} />
                          {M.label}
                        </button>
                      );
                    })}
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => removeRow(r.id)}
                      className="h-8 w-8 p-0 rounded-md border-slate-200 dark:border-slate-700 hover:border-red-300 hover:text-red-600"
                      data-testid={`feedback-delete-${r.id}`}
                      aria-label="Delete feedback"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </AppLayout>
  );
}
